import { Link } from "react-router-dom";
import { Show } from "@clerk/react";

import GoldHero from "../components/landing/GoldHero";
import Reveal from "../components/landing/Reveal";
import "./Home.css";

/**
 * The public landing page.
 *
 * Everything it claims is something the product actually does. GoldBot's
 * pages report the state of a running trading system, so the page is written
 * about that instrument panel — status, positions, strategies, the event log,
 * closed-trade performance — and not about returns. There are no figures, no
 * customer counts and no testimonials, because there is nothing true to put in
 * their place, and a landing page that invents them is the one thing a
 * trading product cannot recover from.
 *
 * The platform is invite-only while it is in development, so the page never
 * offers a signup or a checkout. The call to action depends on whether Clerk
 * has a session: a visitor is offered sign-in, a signed-in user is sent to the
 * control center. `<Show>` reads Clerk's state only — what the account may
 * actually open is decided by the backend when `/goldbot` loads.
 */
function Home() {
  return (
    <main className="lp">
      <GoldHero>
        <div className="lp-lockup">
          <IngotMark />
          <span className="lp-lockup__word">
            GoldBot<span className="lp-lockup__by">by OMJ VISION</span>
          </span>
        </div>

        {/* The accent sits on the first word. At this size its job is to be
            where the eye lands, not to underline the argument: "Gold" opens
            the line, carries the brand, and answers the mark above it. The
            line then resolves into plain white, which a second accent would
            spoil — two highlights in five words and neither means anything. */}
        <h1 className="lp-hero__title">
          <em>Gold</em>, engineered
          <br />
          with discipline.
        </h1>

        <p className="lp-hero__lead">
          GoldBot transforms a defined trading strategy into an automated
          system — built to execute consistently, operate continuously, and
          make every decision measurable.
        </p>

        <div className="lp-actions">
          <Show when="signed-out">
            <Link className="lp-btn lp-btn--primary" to="/sign-in">Sign in</Link>
            <Link className="lp-btn" to="/dashboard">View market prices</Link>
          </Show>
          <Show when="signed-in">
            <Link className="lp-btn lp-btn--primary" to="/goldbot">Open GoldBot</Link>
            <Link className="lp-btn" to="/dashboard">View market prices</Link>
          </Show>
        </div>

        <p className="lp-hero__access">
          Part of the OMJ vision — building intelligent systems where
          technology, automation, and disciplined execution work as one.
        </p>
      </GoldHero>

      <section className="lp-section lp-telemetry">
        <div className="lp-section__inner">
          <Reveal className="lp-telemetry__statement">
            <h2>Instrumented, not advertised.</h2>
            <p>
              Every figure in the product is read from the live system. When a
              value cannot be read, the interface says so and shows a dash
              rather than filling the gap. That rule is the whole design.
            </p>
          </Reveal>

          <Reveal className="lp-telemetry__list" as="dl" delay={80}>
            {TELEMETRY.map((item) => (
              <div className="lp-telemetry__row" key={item.term}>
                <dt>{item.term}</dt>
                <dd>{item.detail}</dd>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="lp-section lp-console">
        <div className="lp-section__inner">
          <Reveal className="lp-console__head">
            <h2>The control center.</h2>
            <p>
              Five surfaces over one running system. Signing in identifies you;
              what you can open is decided server-side, per account.
            </p>
          </Reveal>

          <Reveal className="lp-console__frame" delay={100}>
            <div className="lp-window" aria-hidden="true">
              <div className="lp-window__bar">
                <span className="lp-window__dot" />
                <span className="lp-window__title">GoldBot Control Center</span>
              </div>
              <div className="lp-window__body">
                <nav className="lp-window__rail">
                  {SURFACES.map((surface, index) => (
                    <span key={surface} className={index === 0 ? "is-current" : undefined}>
                      {surface}
                    </span>
                  ))}
                </nav>
                <div className="lp-window__panel">
                  {SLOTS.map((slot) => (
                    <div className="lp-slot" key={slot}>
                      <span className="lp-slot__label">{slot}</span>
                      <span className="lp-slot__value">—</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <p className="lp-console__caption">
              Shown unread. In the product each slot carries a value the system
              reported, with the time it was read.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="lp-section lp-access">
        <div className="lp-section__inner">
          <Reveal className="lp-access__card">
            <div>
              <h2>Access is granted, not bought.</h2>
              <p>
                There is no public signup and no checkout. An administrator
                creates the account and decides what it may open; the backend
                re-checks that on every request.
              </p>
            </div>
            <div className="lp-actions">
              <Show when="signed-out">
                <Link className="lp-btn lp-btn--primary" to="/sign-in">Sign in</Link>
                <Link className="lp-btn" to="/about">About the platform</Link>
              </Show>
              <Show when="signed-in">
                <Link className="lp-btn lp-btn--primary" to="/goldbot">Open GoldBot</Link>
                <Link className="lp-btn" to="/about">About the platform</Link>
              </Show>
            </div>
          </Reveal>
        </div>
      </section>

      <footer className="lp-footer">
        <div className="lp-section__inner lp-footer__inner">
          <div className="lp-lockup lp-lockup--small">
            <IngotMark />
            <span className="lp-lockup__word">GoldBot</span>
          </div>
          <nav className="lp-footer__links" aria-label="Public pages">
            <Link to="/dashboard">Market</Link>
            <Link to="/converter">Converter</Link>
            <Link to="/about">About</Link>
          </nav>
          <p className="lp-footer__note">
            GoldBot reports the state of a running trading system. Nothing here
            is investment advice, and no performance is promised.
          </p>
        </div>
      </footer>
    </main>
  );
}

/* What the control center actually reports, in the order the product presents
   it. No figure is quoted here, because every one of them belongs to a running
   system and would be a fabrication on a marketing page. */
const TELEMETRY = [
  { term: "Live status", detail: "Whether the system is running, and how long it has been up." },
  { term: "Open positions", detail: "Every position the system is holding, while it holds it." },
  { term: "Active strategies", detail: "Which strategies are enabled, and the state each one is in." },
  { term: "Event log", detail: "A running record of what the system did, and when it did it." },
  { term: "Closed-trade performance", detail: "Recorded results from trades that have actually closed." },
];

const SURFACES = ["Overview", "Positions", "Strategies", "Logs", "Performance"];
const SLOTS = ["Status", "Uptime", "Open positions", "Last event"];

/**
 * The brand mark: the same cast ingot the hero builds its stack from, reduced
 * to a single silhouette. Drawn, so it carries the palette and holds up at
 * 20px — which is the whole reason the page does not use an emoji here.
 */
function IngotMark() {
  return (
    <svg className="lp-mark" viewBox="0 0 24 20" aria-hidden="true" focusable="false">
      <path d="M6.6 3.2 H17.4 L21 9.4 H3 Z" fill="#f1bd51" />
      <path d="M3 9.4 H21 L19.4 16.4 H4.6 Z" fill="#b8862a" />
      <path d="M3 9.4 H21" stroke="#fff3cd" strokeOpacity="0.55" strokeWidth="0.9" />
    </svg>
  );
}

export default Home;
